<template>
  <div class="sector-select" :class="{ 'sector-select--compact': compact }">
    <div class="sector-select__control" @click="toggle">
      <template v-if="modelValue && modelValue.length">
        <span v-for="tok in modelValue" :key="tok" class="sector-chip" @click.stop>
          {{ chipLabel(tok) }}
          <button type="button" class="sector-chip__x" title="Remove" @click.stop="remove(tok)">&times;</button>
        </span>
      </template>
      <span v-else class="sector-select__placeholder">{{ placeholder }}</span>
      <CygnusIcon name="chevron-down" class="sector-select__caret" />
    </div>

    <div v-if="open" class="sector-select__panel" @click.stop>
      <input
        ref="search"
        v-model="query"
        type="text"
        class="form-control form-control-sm sector-select__search"
        placeholder="Search industry or SIC code…"
      />
      <div class="sector-select__list">
        <div v-if="loading" class="sector-select__empty">Loading…</div>
        <div v-else-if="error" class="sector-select__empty">Could not load industries.</div>
        <template v-else>
          <!-- Whole-section picks -->
          <div v-if="visibleSections.length" class="sector-select__group">Sections</div>
          <button
            v-for="s in visibleSections"
            :key="'sec-' + s.section"
            type="button"
            class="sector-select__opt"
            :class="{ 'is-selected': isSelected(s.section) }"
            @click="toggleToken(s.section)"
          >
            <span class="sector-select__code">{{ s.section }}</span>
            <span class="sector-select__lbl">{{ s.section_label }}</span>
            <span class="sector-select__all">whole section</span>
          </button>

          <!-- Individual SIC codes -->
          <div v-if="visibleCodes.length" class="sector-select__group">SIC codes</div>
          <button
            v-for="c in visibleCodes"
            :key="'code-' + c.code"
            type="button"
            class="sector-select__opt"
            :class="{ 'is-selected': isSelected(c.code) }"
            @click="toggleToken(c.code)"
          >
            <span class="sector-select__code">{{ c.code }}</span>
            <span class="sector-select__lbl">{{ c.label }}</span>
            <span v-if="c.section" class="sector-select__sec">{{ c.section }}</span>
          </button>

          <div v-if="!visibleSections.length && !visibleCodes.length" class="sector-select__empty">No match.</div>
          <div v-if="truncated" class="sector-select__more">Refine your search to see more…</div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * MusubiSectorSelect — searchable multi-select for UK SIC 2007 industries,
 * backed by GET /api/v1/admin/leads/sectors. v-model is an array of string
 * tokens: a SIC section letter ("F", = the whole of Construction) or a full SIC
 * code ("41100"). Shared by the prospects list filter + the drip audience
 * builder so both express industry targeting identically. The tokens are sent
 * verbatim to the backend sector filter, which expands sections to their codes.
 */
import useApi from "@apexl/icarus-core/src/services/api";

const MAX_CODES = 60; // cap the rendered code list for a snappy panel

export default {
  name: 'MusubiSectorSelect',
  props: {
    modelValue:  { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Any industry' },
    compact:     { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      api: useApi(),
      sections: [],
      codes: [],
      loading: false,
      error: null,
      open: false,
      query: '',
      truncated: false,
    };
  },
  computed: {
    // {token: label} for chip rendering (sections + codes).
    labelByToken() {
      const map = {};
      this.sections.forEach((s) => { map[s.section] = `${s.section} — ${s.section_label}`; });
      this.codes.forEach((c) => { map[c.code] = `${c.code} — ${c.label}`; });
      return map;
    },
    visibleSections() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.sections;
      return this.sections.filter((s) =>
        s.section.toLowerCase() === q ||
        s.section_label.toLowerCase().includes(q));
    },
    visibleCodes() {
      const q = this.query.trim().toLowerCase();
      let list = this.codes;
      if (q) {
        list = this.codes.filter((c) =>
          c.code.includes(q) || c.label.toLowerCase().includes(q));
      }
      this.truncated = list.length > MAX_CODES;
      return list.slice(0, MAX_CODES);
    },
  },
  mounted() {
    this.load();
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get('/api/v1/admin/leads/sectors');
        const p = res?.data ?? res;
        this.sections = Array.isArray(p?.sections) ? p.sections : [];
        this.codes = Array.isArray(p?.codes) ? p.codes : [];
      } catch (e) {
        this.error = e?.message || 'load failed';
      }
      this.loading = false;
    },
    chipLabel(tok) {
      return this.labelByToken[tok] || tok;
    },
    isSelected(tok) {
      return (this.modelValue || []).includes(tok);
    },
    toggleToken(tok) {
      const cur = Array.isArray(this.modelValue) ? this.modelValue.slice() : [];
      const i = cur.indexOf(tok);
      if (i === -1) cur.push(tok); else cur.splice(i, 1);
      this.$emit('update:modelValue', cur);
    },
    remove(tok) {
      this.$emit('update:modelValue', (this.modelValue || []).filter((t) => t !== tok));
    },
    toggle() {
      this.open = !this.open;
      if (this.open) this.$nextTick(() => this.$refs.search?.focus());
    },
    onDocClick(ev) {
      if (!this.$el.contains(ev.target)) this.open = false;
    },
  },
};
</script>

<style scoped>
.sector-select { position: relative; }
.sector-select__control {
  display: flex; align-items: center; flex-wrap: wrap; gap: 4px;
  min-height: 31px; padding: 3px 26px 3px 8px; cursor: pointer;
  border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 6px);
  background: var(--m-bg-card, #FFF); font-size: 0.82rem;
}
.sector-select--compact .sector-select__control { min-height: 29px; }
.sector-select__placeholder { color: var(--m-text-tertiary, #9AA0AC); }
.sector-select__caret {
  position: absolute; right: 9px; top: 50%; transform: translateY(-50%);
  font-size: 0.7rem; color: var(--m-text-tertiary, #9AA0AC); pointer-events: none;
}
.sector-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 4px 1px 7px; border-radius: 100px;
  background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5);
  font-size: 0.72rem; font-weight: 500; max-width: 220px;
}
.sector-chip__x {
  border: 0; background: transparent; color: inherit; cursor: pointer;
  font-size: 0.95rem; line-height: 1; padding: 0 2px;
}
.sector-chip__x:hover { color: var(--m-status-danger, #EF4444); }

.sector-select__panel {
  position: absolute; z-index: 40; top: calc(100% + 3px); left: 0;
  width: 340px; max-width: 90vw;
  background: var(--m-bg-card, #FFF); border: 1px solid var(--m-border-strong, #C7CEDC);
  border-radius: var(--m-radius, 8px); box-shadow: var(--m-shadow-panel, 0 12px 32px rgba(15, 23, 42, .12));
  padding: 8px;
}
.sector-select__search { margin-bottom: 6px; }
.sector-select__list { max-height: 300px; overflow-y: auto; }
.sector-select__group {
  font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--m-text-tertiary, #9AA0AC); padding: 6px 6px 3px;
}
.sector-select__opt {
  display: flex; align-items: baseline; gap: 8px; width: 100%; text-align: left;
  border: 0; background: transparent; cursor: pointer;
  padding: 5px 6px; border-radius: var(--m-radius, 6px); font-size: 0.8rem;
  color: var(--m-text-primary, #0F172A);
}
.sector-select__opt:hover { background: var(--m-bg-subtle, #F8FAFC); }
.sector-select__opt.is-selected { background: var(--m-accent-soft, #EEF0FF); }
.sector-select__code {
  flex: 0 0 auto; font-variant-numeric: tabular-nums; font-weight: 600;
  color: var(--m-accent, #4F46E5); min-width: 42px;
}
.sector-select__lbl {
  flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: var(--m-text-secondary, #475569);
}
.sector-select__all, .sector-select__sec {
  flex: 0 0 auto; font-size: 0.66rem; color: var(--m-text-tertiary, #9AA0AC);
}
.sector-select__empty, .sector-select__more {
  padding: 8px 6px; font-size: 0.76rem; color: var(--m-text-tertiary, #9AA0AC); font-style: italic;
}
</style>
